// Which work-order form fields block saving, and what is sent to the API. Optional fields
// that were touched by accident (a stray quote note, an empty extra item, the other
// customer tab) must never block the order.
import type { WorkOrderInput } from './types';

export type CustomerMode = 'existing' | 'new';

type Item = WorkOrderInput['items'][number];

/** A quote only counts once it has an amount; notes on their own are ignored. */
export function hasQuoteAmount(quote: WorkOrderInput['quote']): quote is NonNullable<WorkOrderInput['quote']> {
  return Boolean(quote && quote.amount > 0);
}

/** A new item row nobody filled in, e.g. from an accidental "Add item". Saved items are never blank. */
export function isBlankItem(item: Item): boolean {
  return !item.id && !item.description.trim() && item.departmentIds.length === 0;
}

function itemsToSave(items: Item[]): Item[] {
  const filled = items.filter((item) => !isBlankItem(item));
  return filled.length ? filled : items;
}

/** Labels of the required fields still missing, in form order. Empty means ready to save. */
export function missingFields(input: WorkOrderInput, mode: CustomerMode): string[] {
  const missing: string[] = [];
  if (mode === 'existing' ? !input.clientId : !input.newCustomer?.name.trim()) {
    missing.push(mode === 'existing' ? 'customer' : 'customer name');
  }
  if (!input.orderDate) missing.push('work-order date');
  const items = itemsToSave(input.items);
  const numbered = items.length > 1;
  for (const item of items) {
    const label = numbered ? `item ${input.items.indexOf(item) + 1}` : 'item';
    if (!item.description.trim()) missing.push(`${label} description`);
    if (!item.departmentIds.length) missing.push(`${label} departments`);
  }
  if (!input.deliveryDate) missing.push('delivery date');
  if (!input.contactPerson.trim()) missing.push('graphic designer');
  return missing;
}

/** The payload for create/update: drops the unused customer tab, blank new items and an amount-less quote. */
export function prepareWorkOrderInput(input: WorkOrderInput, mode: CustomerMode): WorkOrderInput {
  return {
    ...input,
    clientId: mode === 'existing' ? input.clientId : undefined,
    newCustomer: mode === 'new' ? input.newCustomer : undefined,
    items: itemsToSave(input.items),
    quote: hasQuoteAmount(input.quote) ? input.quote : undefined,
  };
}
